import * as React from 'react';
import { Button } from './Button';
import { MultiSelectInput } from './MultiSelectInput';
import { getTagOptionNames, checkNewTagName } from './UserTagEditor.logic';
import './UserTagEditor.css';

interface UserTagEditorProps {
  tags: string[];
  onChange: (tags: string[]) => void;
  // ほかの物件で使われているタグの名前
  existingTagNames: string[];
}

// タグのチェックリストの1項目
interface TagOption {
  name: string;
}

// 物件のタグの編集欄。表記ゆれでタグが増えないよう、基本は既存のタグから選んでもらう。
// 選び方は物件の作業者の欄と同じく、選択欄の形の欄を押して開くチェックリストで付け外しする。
// 新しいタグは、欄の右の「新規タグ作成」で開く入力欄か、チェックリストの検索欄に入れた文字に当てはまるタグがないときに出る行で作る。
// 表記だけが違う既存のタグ（全角半角・大文字小文字・空白の違い）は、新しくは作らせずにそちらを使ってもらう
export const UserTagEditor: React.FC<UserTagEditorProps> = function (props) {
  const [creating, setCreating] = React.useState<boolean>(false);
  const [newTagName, setNewTagName] = React.useState<string>('');
  const [message, setMessage] = React.useState<string>('');
  const [similarTagName, setSimilarTagName] = React.useState<string>();

  const tagOptions: TagOption[] = getTagOptionNames(props.existingTagNames, props.tags).map(name => ({ name }));

  const addTag = (name: string) => {
    if (props.tags.indexOf(name) === -1) {
      props.onChange(props.tags.concat([name]));
    }
  };

  // チェックリストで選んだタグをまとめて付け外しする。付けるときは、すでに付いているタグを重ねない
  const setTagsSelected = (options: TagOption[], selected: boolean) => {
    const names = options.map(option => option.name);
    if (selected) {
      const addedNames = names.filter(name => props.tags.indexOf(name) === -1);
      props.onChange(props.tags.concat(addedNames));
      return;
    }
    props.onChange(props.tags.filter(tag => names.indexOf(tag) === -1));
  };

  const closeCreating = () => {
    setCreating(false);
    setNewTagName('');
    setMessage('');
    setSimilarTagName(undefined);
  };

  const createTag = () => {
    const result = checkNewTagName(newTagName, props.tags, props.existingTagNames);
    if (result.kind === 'error') {
      setMessage(result.message);
      setSimilarTagName(undefined);
      return;
    }
    if (result.kind === 'similar') {
      setMessage(`既存のタグ「${result.existingName}」と同じ名前とみなされるため、新しくは作れません`);
      setSimilarTagName(result.existingName);
      return;
    }
    addTag(result.name);
    closeCreating();
  };

  let createButton;
  if (!creating) {
    createButton = (
      <Button mini onClick={() => setCreating(true)}>
        新規タグ作成
      </Button>
    );
  }

  let createForm;
  if (creating) {
    let messageRow;
    if (message !== '') {
      let useSimilarButton;
      // 新しいタグを作るのではなく、既存のタグをこの物件に付けるボタンであることが分かる文言にする
      if (similarTagName !== undefined) {
        useSimilarButton = (
          <Button
            mini
            color="white"
            onClick={() => {
              addTag(similarTagName);
              closeCreating();
            }}
          >
            既存の「{similarTagName}」を使う
          </Button>
        );
      }
      messageRow = (
        <div className="UserTagEditor-message">
          <span>{message}</span>
          {useSimilarButton}
        </div>
      );
    }
    createForm = (
      <div className="UserTagEditor-create">
        <div className="UserTagEditor-row">
          <input
            className="UserTagEditor-input"
            type="text"
            placeholder="新しいタグ名"
            value={newTagName}
            autoFocus
            onChange={event => {
              setNewTagName(event.target.value);
              setMessage('');
              setSimilarTagName(undefined);
            }}
            onKeyDown={event => {
              if (event.key === 'Enter') {
                event.preventDefault();
                createTag();
              }
            }}
          />
          <Button mini onClick={createTag}>作成して追加</Button>
          <Button mini color="white" onClick={closeCreating}>キャンセル</Button>
        </div>
        {messageRow}
      </div>
    );
  }

  return (
    <div className="UserTagEditor-container">
      <div className="UserTagEditor-row">
        <MultiSelectInput
          label="タグ"
          placeholder="タグを選択"
          items={tagOptions}
          getKey={option => option.name}
          isSelected={option => props.tags.indexOf(option.name) !== -1}
          onSetSelected={setTagsSelected}
          createOption={{
            canCreate: query => checkNewTagName(query, props.tags, props.existingTagNames).kind === 'ok',
            getLabel: query => `＋「${query}」を新しいタグとして作る`,
            onCreate: query => {
              const result = checkNewTagName(query, props.tags, props.existingTagNames);
              if (result.kind !== 'ok') {
                return;
              }
              addTag(result.name);
            },
          }}
        />
        {createButton}
      </div>
      {createForm}
    </div>
  );
};
